import { z } from 'zod';
import { HHMM_RE } from './settings.siteWorkerDay.schema.js';
import { hhmmMinutes as m } from './fieldGpsSamples.schema.js';

/**
 * WHEN THE FIELD TABLETS TAKE THEIR SCHEDULED GPS READINGS (owner, 2026-10-29).
 *
 * Not punches: a reading every `everyMinutes` inside two windows of COMPANY
 * time, plus one reading `everyMinutes` before each window opens, so the
 * console can show each tablet's GPS pattern over the hours its site is busy
 * (Devices → Performance, the GPS column). The device learns the windows on every `/field/sync`
 * as `gpsSampleWindows` + `gpsSampleEveryMin` (packages/api/src/services/
 * field.gpsSampling.ts) and uploads to `POST /api/field/gps-samples`
 * (fieldGpsSamples.schema.ts).
 *
 * Nested under `faceRecognition` because that is the category the device
 * policy already lives in (`fallbackToPin`, `maxAttempts`) and the one the
 * Settings → Attendance → tablets sections write; its own module because
 * settings.schema.ts is at its size ceiling and may only shrink.
 *
 * Two named windows, not a list: the console splits every figure Morning /
 * Evening, and a third window would need a third column nobody has drawn.
 * Neither window may cross midnight, the lead reading included, and the two
 * may not overlap — see the refinements below, which the API enforces on save.
 */
export const GpsSamplingSettingsSchema = z
  .object({
    morningStart: z.string().regex(HHMM_RE, 'Expected HH:MM').default('05:30'),
    morningEnd: z.string().regex(HHMM_RE, 'Expected HH:MM').default('07:00'),
    eveningStart: z.string().regex(HHMM_RE, 'Expected HH:MM').default('16:00'),
    eveningEnd: z.string().regex(HHMM_RE, 'Expected HH:MM').default('20:00'),
    everyMinutes: z.number().int().min(1).max(60).default(5),
  })
  .superRefine((s, ctx) => {
    if (m(s.morningStart) - s.everyMinutes < 0 || m(s.morningStart) >= m(s.morningEnd)) {
      ctx.addIssue({ code: 'custom', path: ['morningEnd'], message: 'The morning window must end after it starts, on the same day' });
    }
    if (m(s.eveningStart) >= m(s.eveningEnd)) {
      ctx.addIssue({ code: 'custom', path: ['eveningEnd'], message: 'The evening window must end after it starts, on the same day' });
    }
    if (m(s.eveningStart) - s.everyMinutes <= m(s.morningEnd)) {
      ctx.addIssue({ code: 'custom', path: ['eveningStart'], message: 'The evening window must start after the morning window ends' });
    }
  });
export type GpsSamplingSettings = z.infer<typeof GpsSamplingSettingsSchema>;
